
import { Vue, Component, Prop, Watch } from "vue-property-decorator";

import GET_TAGS from "@/apollo/autocomplete-queries/GetTags.gql";
import createTagMutation from "@/apollo/autocomplete-queries/CreateTags.gql";
import DropdownFilter from "@/components/filters/DropdownFilterNew.vue";
import TagMultiselectOption from "@/entities/TagMultiselectOption";

import filterMultiselectItems from "@/helpers/multiselect/filterMultiselectItemsTags";
import { watch } from "fs";

@Component({
  components: {
    DropdownFilter,
    createTagMutation,
  },
  apollo: {
    tags: {
      query: GET_TAGS,
      variables() {
        return this.tagAutocompleteFilter;
      },
      update(data) {
        this.tags.length = 0;
        Array.prototype.push.apply(this.tags, data.tag);
        this.isLoadingTags = false;
        return data.tag;
      },
      skip() {
        return !this.tagSearchString;
      },
    },
  },
})
export default class TagsMultiselect extends Vue {
  @Prop({ type: Array, required: true, default: undefined }) readonly value!:
    | string[]
    | [];
  @Prop({ type: Boolean, default: true }) readonly withBorder!: boolean;
  @Prop({ type: Number, default: 5 }) readonly limit!: number;
  @Prop({ type: Number, default: undefined }) readonly max?: number;
  @Prop({ type: Object, default: () => ({}) }) readonly condition!: Object;
  @Prop({ type: String, default: undefined }) readonly currentUserId?: string;
  @Prop({ type: String, default: undefined }) readonly companyId?: string;
  @Prop({ type: Boolean, default: false }) readonly enableTagsAdding!: boolean;
  @Prop({ type: Boolean, default: false }) readonly withCustomTags!: boolean;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ type: String, default: "" })
  readonly getParentcontainerStyle?: string;

  tags: TagMultiselectOption[] | any = [];
  createTagMutation = createTagMutation;
  tagSearchString: string = "";
  chosenTags: TagMultiselectOption[] = [];
  newTagsNames: string[] = [];
  isLoadingTags: boolean = false;

  get tagsOptions(): TagMultiselectOption[] {
    if (!this.withCustomTags) {
      return this.tags;
    }

    const searchTagAlreadyChosen = this.tags.some(
      ({ name }) => name.toLowerCase() === this.tagSearchString.toLowerCase()
    );

    if (searchTagAlreadyChosen || this.tagFoundInList) {
      return this.tags;
    }

    return [
      {
        id: 0 - Date.now(),
        name: this.tagSearchString,
      },
      ...this.tags,
    ];
  }

  get tagAutocompleteFilter() {
    const conditions: any[] = [];
    if (this.tagSearchString.length > 0) {
      conditions.push({
        name: {
          _ilike: `%${this.tagSearchString}%`,
        },
      });
    }
    if (this.value.length) {
      conditions.push({
        id: {
          _nin: this.value,
        },
      });
    }
    return {
      where: { _and: [...conditions] },
    };
  }

  @Watch("tagSearchString")
  onTagSearchStringChanged() {
    this.isLoadingTags = true;
  }

  mounted() {
    this.$watch(
      () => this.tags,
      (newTags, oldTags) => {
        if (newTags !== oldTags && this.isLoadingTags) {
          this.isLoadingTags = false;
        }
      }
    );
  }

  @Watch("value", { immediate: true })
  async onTagsIdsChanged(): Promise<void> {
    const tagsIds = this.value;
    if (tagsIds.length === 0) {
      this.chosenTags = [];
      return;
    }

    const {
      newItemsIds: newTagIds,
      previouslyChosenItems: previouslyChosenTags,
    } = filterMultiselectItems(tagsIds, this.chosenTags);

    if (newTagIds.length === 0) {
      return;
    }

    const rawData = await this.$apollo.query({
      query: GET_TAGS,
      variables: {
        where: {
          id: {
            _in: newTagIds,
          },
        },
      },
    });

    const newChosenTags = rawData.data.tag;
    this.chosenTags = [...previouslyChosenTags, ...newChosenTags];
  }

  get tagFoundInList(): boolean {
    return this.tags.some(
      ({ name }) => name.toLowerCase() === this.tagSearchString.toLowerCase()
    );
  }

  async createNewTag(tagName: string): Promise<TagMultiselectOption | null> {
    try {
      const { data } = await this.$apollo.mutate({
        mutation: this.createTagMutation,
        variables: {
          name: tagName,
        },
      });

      const newTag = data.insert_tag.returning[0];

      this.tags.push(newTag);

      return newTag;
    } catch (error) {
      return null;
    }
  }

  async updateSearch(searchValue: string): Promise<void> {
    this.tagSearchString = searchValue;

    this.$emit("inputTags", this.chosenTags);

    this.$emit("search", searchValue);
  }

  isNewTag(tag: TagMultiselectOption): boolean {
    if (tag.id >= 0) {
      return false;
    }
    if(tag.id <0){
      return true;
    }
    // return (
    //   !this.tagFoundInList ||
    //   this.newTagsNames.some((tagName) => tagName === tag.name)
    // );
  }

  handleInput(chosenTags: TagMultiselectOption[]): void {
    this.chosenTags = chosenTags;

    const newTag = chosenTags.find((tag) => tag.id < 0);

    if (newTag) {
      this.createNewTag(newTag.name).then((createdTag) => {
        if (createdTag) {
          // Select the newly created tag
          this.chosenTags = [
            ...chosenTags.filter((tag) => tag !== newTag),
            createdTag,
          ];
          this.$emit("input", this.chosenTags, newTag);
        }
      });
    } else {
      // Existing tags are selected
      const chosenTagsIds = chosenTags.map((option) => option.id);
      this.$emit("input", chosenTagsIds, newTag);
    }
  }
}
